import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { useNotification } from '../context/NotificationContext';
import { useUsers } from '../context/UsersContext';
import AdvancedFilterDrawer from '../components/AdvancedFilterDrawer';
import MonthYearPicker from '../components/MonthYearPicker';
import SearchableDropdown from '../components/SearchableDropdown';
import ColumnSelector from '../components/ColumnSelector';
import Tooltip from '../components/Tooltip';
import UserTimestamp from '../components/UserTimestamp';
import { formatDateRange, formatDateTime, getMonthRange, getCurrentMonthYear, formatMonthYear, formatDateUS } from '../utils/dateUtils';
import { filterEmojis } from '../utils/emojiFilter';
import { loadVisibleColumns, persistVisibleColumns } from '../utils/columnPreferences';
import { useResizableTableColumns } from '../hooks/useResizableTableColumns';
import { CellEllipsis } from '../components/CellEllipsis';
import { ResizableTh } from '../components/ResizableTableHeader';
import { Copy, RotateCcw } from 'lucide-react';
import './Clarifications.css';

const DRL_CLARIFICATIONS_COLUMN_PREFS_KEY = 'drlClarificationsTable';
const DRL_CLAR_TABLE_WIDTHS_KEY = 'drlClarificationsTableColWidths';
const DRL_CLAR_DEFAULT_COL_WIDTH_PX = {
  drlNumber: 124,
  clarificationType: 120,
  drlReceivedDate: 112,
  amount: 96,
  assigneeType: 104,
  assignee: 124,
  reporteeType: 104,
  reporter: 124,
  status: 104,
  reconciliationStatus: 140,
  openedAt: 112,
  resolvedAt: 112,
  details: 168,
  createdAt: 112,
  updatedAt: 112
};

const DrlClarifications = () => {
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  
  // State management
  const [clarifications, setClarifications] = useState([]);
  const [allClarifications, setAllClarifications] = useState([]); // Store all fetched clarifications before filtering
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Storage key for persisting filters
  const STORAGE_KEY = 'drlClarificationsFiltersState';

  // Helper function to load filters from sessionStorage (called once during initialization)
  const loadInitialFilters = () => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading filters from sessionStorage:', error);
    }
    return null;
  };

  const initialFilters = loadInitialFilters();

  // Pagination - initialize from storage if available
  const [currentPage, setCurrentPage] = useState(initialFilters?.currentPage ?? 0);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [pageInputValue, setPageInputValue] = useState('');
  
  // Default filters - initialize from storage if available
  const [searchTerm, setSearchTerm] = useState(initialFilters?.searchTerm ?? '');
  const [selectedStatus, setSelectedStatus] = useState(initialFilters?.selectedStatus ?? '');
  const { getUserName, getUserById } = useUsers(); // Get users from context
  const [practices, setPractices] = useState([]);
  
  // Column selector
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const columnSelectorTriggerRef = useRef(null);
  
  // Available columns
  const allColumns = [
    { key: 'drlNumber', label: 'DRL Number' },
    { key: 'clarificationType', label: 'Type' },
    { key: 'drlReceivedDate', label: 'DRL Received Date' },
    { key: 'amount', label: 'Amount' },
    { key: 'assigneeType', label: 'Assignee' },
    { key: 'assignee', label: 'Assignee Name' },
    { key: 'reporteeType', label: 'Reporter' },
    { key: 'reporter', label: 'Reporter Name' },
    { key: 'status', label: 'Status' },
    { key: 'reconciliationStatus', label: 'Reconciliation Status' },
    { key: 'openedAt', label: 'Opened Date' },
    { key: 'resolvedAt', label: 'Resolved Date' },
    { key: 'details', label: 'Details' },
    { key: 'createdAt', label: 'Created At' },
    { key: 'updatedAt', label: 'Updated At' }
  ];

  // Default visible columns - drlNumber, type, drlReceivedDate, amount, assigneeType, reporteeType, status, openedAt, resolvedAt, updatedAt
  const defaultColumns = [
    'drlNumber',
    'clarificationType',
    'drlReceivedDate',
    'amount',
    'assigneeType',
    'reporteeType',
    'status',
    'openedAt',
    'resolvedAt',
    'updatedAt'
  ];

  const [visibleColumns, setVisibleColumns] = useState(() =>
    loadVisibleColumns(
      DRL_CLARIFICATIONS_COLUMN_PREFS_KEY,
      new Set(allColumns.map(c => c.key)),
      defaultColumns
    )
  );

  const drlClarColumnOrder = useMemo(() => [...visibleColumns], [visibleColumns]);
  const { Colgroup, onResizeMouseDown, resetColumnWidths } = useResizableTableColumns({
    storageKey: DRL_CLAR_TABLE_WIDTHS_KEY,
    columnIds: drlClarColumnOrder,
    defaultWidths: DRL_CLAR_DEFAULT_COL_WIDTH_PX,
    minWidth: 48
  });
  
  // Advanced filters - now support arrays for multiple selections
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const defaultAdvancedFilters = {
    statuses: [], // Array for multiple status selections
    practiceCodes: [], // Array for multiple practice selections
    locationCodes: [], // Array for multiple location selections
    assigneeIds: [], // Array for multiple assignee selections
    excludeAssigneeIds: [],
    reporterIds: [], // Array for multiple reporter selections
    excludeReporterIds: [],
    reporteeIds: [], // Array for multiple reportee selections (used for DIT/DRL clarifications)
    excludeReporteeIds: [],
    assigneeTypes: [], // Array for multiple assignee type selections (ONSHORE, OFFSHORE)
    reporteeTypes: [], // Array for multiple reportee type selections (ONSHORE, OFFSHORE)
    reconciliationStatuses: [], // Array for multiple reconciliation status selections
    drlNumber: '',
    clarificationType: '', // Clarification type filter with wildcard support
    unknown: undefined, // undefined = ALL, false = false, true = true
    drlReceivedDateFrom: '',
    drlReceivedDateTo: '',
    resolvedDateFrom: '',
    resolvedDateTo: '',
    openedDateFrom: '',
    openedDateTo: '',
    startDate: '', // Legacy support
    endDate: '', // Legacy support
    month: null,
    year: null
  };
  const [advancedFilters, setAdvancedFilters] = useState(
    initialFilters?.advancedFilters ?? defaultAdvancedFilters
  );
  
  // Sorting - initialize from storage if available
  const [sortField, setSortField] = useState(initialFilters?.sortField ?? 'openedAt');
  const [sortDirection, setSortDirection] = useState(initialFilters?.sortDirection ?? 'desc'); // Default: newest first

  // Month/Year selection - initialize from storage or defaults
  const currentMonthYear = getCurrentMonthYear();
  const [selectedMonth, setSelectedMonth] = useState(
    initialFilters?.selectedMonth ?? currentMonthYear.month
  );
  const [selectedYear, setSelectedYear] = useState(
    initialFilters?.selectedYear ?? currentMonthYear.year
  );
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  
  const handleMonthYearSelect = (month, year) => {
    setSelectedMonth(month);
    setSelectedYear(year);
    setShowMonthPicker(false);
  };

  // Function to save filters to sessionStorage
  const saveFiltersToStorage = useCallback(() => {
    try {
      const filterState = {
        searchTerm,
        selectedStatus,
        advancedFilters,
        selectedMonth,
        selectedYear,
        sortField,
        sortDirection,
        currentPage
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filterState));
    } catch (error) {
      console.error('Error saving filters to sessionStorage:', error);
    }
  }, [searchTerm, selectedStatus, advancedFilters, selectedMonth, selectedYear, sortField, sortDirection, currentPage]);

  const openDrlClarificationDetail = useCallback(
    (row) => {
      if (!row?.drlId) return;
      const completeFilters = {
        searchTerm,
        selectedStatus,
        advancedFilters,
        selectedMonth,
        selectedYear,
        sortField,
        sortDirection,
        currentPage
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(completeFilters));
      saveFiltersToStorage();
      navigate(
        `/dit-drl/${row.drlId}?clarificationId=${row.clarificationId}&source=drl-clarifications`
      );
    },
    [
      searchTerm,
      selectedStatus,
      advancedFilters,
      selectedMonth,
      selectedYear,
      sortField,
      sortDirection,
      currentPage,
      navigate,
      saveFiltersToStorage
    ]
  );

  const handleCopyDrlNumber = useCallback(
    async (drlNumber) => {
      if (!drlNumber) return;
      try {
        await navigator.clipboard.writeText(drlNumber);
        showSuccess('DRL number copied');
      } catch (err) {
        console.error('Copy failed:', err);
        showError('Could not copy DRL number');
      }
    },
    [showSuccess, showError]
  );

  // Save filters to sessionStorage whenever they change (skip initial mount to avoid overwriting)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return; // Skip saving on initial mount
    }
    saveFiltersToStorage();
  }, [saveFiltersToStorage]);

  // Restore filters when navigating back to /clarifications/drl page
  const hasRestoredFromStorage = useRef(false);
  useEffect(() => {
    if (location.pathname === '/clarifications/drl' && !hasRestoredFromStorage.current) {
      const stored = loadInitialFilters();
      if (stored) {
        // Only restore if current state doesn't match stored state (to avoid unnecessary updates)
        if (stored.searchTerm !== searchTerm) setSearchTerm(stored.searchTerm || '');
        if (stored.selectedStatus !== selectedStatus) setSelectedStatus(stored.selectedStatus || '');
        if (JSON.stringify(stored.advancedFilters) !== JSON.stringify(advancedFilters)) {
          setAdvancedFilters(stored.advancedFilters || defaultAdvancedFilters);
        }
        if (stored.selectedMonth !== selectedMonth) setSelectedMonth(stored.selectedMonth ?? currentMonthYear.month);
        if (stored.selectedYear !== selectedYear) setSelectedYear(stored.selectedYear ?? currentMonthYear.year);
        if (stored.sortField !== sortField) setSortField(stored.sortField || 'openedAt');
        if (stored.sortDirection !== sortDirection) setSortDirection(stored.sortDirection || 'desc');
        if (stored.currentPage !== currentPage) setCurrentPage(stored.currentPage ?? 0);
      }
      hasRestoredFromStorage.current = true;
    }
    // Reset the flag when navigating away
    if (location.pathname !== '/clarifications/drl') {
      hasRestoredFromStorage.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Removed: Users are now loaded from context on login, no need to fetch here

  // Fetch practices on mount
  useEffect(() => {
    fetchPractices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPractices = async () => {
    try {
      const response = await api.getAllPractices();
      const practicesList = Array.isArray(response) ? response : (response?.items || []);
      setPractices(practicesList.filter(p => p.isActive !== false));
    } catch (error) {
      console.error('Error fetching practices:', error);
      const errorMessage = error?.message || error?.data?.message || 'Failed to fetch practices';
      showError(errorMessage);
    }
  };

  const fetchClarifications = useCallback(async (page = 0) => {
    setLoading(true);
    setError(null);
    
    try {
      // Build search parameters for POST request
      const searchParams = {
        page: page,
        size: 50
      };

      // Collect statuses from both default and advanced filters
      const statuses = [];
      if (selectedStatus && selectedStatus.trim() !== '') {
        statuses.push(selectedStatus.trim());
      }
      if (advancedFilters.statuses && Array.isArray(advancedFilters.statuses) && advancedFilters.statuses.length > 0) {
        // Filter out empty strings and add valid statuses
        const validStatuses = advancedFilters.statuses
          .filter(status => status && typeof status === 'string' && status.trim() !== '')
          .map(status => status.trim());
        statuses.push(...validStatuses);
      }
      // Remove duplicates and empty values, then set if we have any valid statuses
      const uniqueStatuses = [...new Set(statuses)].filter(s => s && s.trim() !== '');
      if (uniqueStatuses.length > 0) {
        searchParams.statuses = uniqueStatuses;
      }

      // Practice codes (array)
      if (advancedFilters.practiceCodes && advancedFilters.practiceCodes.length > 0) {
        searchParams.practiceCodes = [...new Set(advancedFilters.practiceCodes)];
      }

      // Location codes (array)
      if (advancedFilters.locationCodes && advancedFilters.locationCodes.length > 0) {
        searchParams.locationCodes = [...new Set(advancedFilters.locationCodes)];
      }

      // Assignee IDs (array)
      if (advancedFilters.assigneeIds && advancedFilters.assigneeIds.length > 0) {
        searchParams.assigneeIds = [...new Set(advancedFilters.assigneeIds)];
      }
      if (advancedFilters.excludeAssigneeIds && advancedFilters.excludeAssigneeIds.length > 0) {
        searchParams.excludeAssigneeIds = [...new Set(advancedFilters.excludeAssigneeIds)];
      }

      // Reporter IDs (array)
      // For DIT/DRL clarifications, the AdvancedFilterDrawer uses reporteeIds instead of reporterIds
      // Check both and combine them, then map to reporterIds for the API
      const reporterIds = [];
      if (advancedFilters.reporterIds && advancedFilters.reporterIds.length > 0) {
        reporterIds.push(...advancedFilters.reporterIds);
      }
      if (advancedFilters.reporteeIds && advancedFilters.reporteeIds.length > 0) {
        reporterIds.push(...advancedFilters.reporteeIds);
      }
      if (reporterIds.length > 0) {
        searchParams.reporterIds = [...new Set(reporterIds)];
      }
      const excludeReporterIds = [];
      if (advancedFilters.excludeReporterIds && advancedFilters.excludeReporterIds.length > 0) {
        excludeReporterIds.push(...advancedFilters.excludeReporterIds);
      }
      if (advancedFilters.excludeReporteeIds && advancedFilters.excludeReporteeIds.length > 0) {
        excludeReporterIds.push(...advancedFilters.excludeReporteeIds);
      }
      if (excludeReporterIds.length > 0) {
        searchParams.excludeReporterIds = [...new Set(excludeReporterIds)];
      }

      // Assignee Types (array) - Transform ONSHORE/OFFSHORE to ON-SHORE/OFF-SHORE for API
      if (advancedFilters.assigneeTypes && advancedFilters.assigneeTypes.length > 0) {
        searchParams.assigneeTypes = [...new Set(advancedFilters.assigneeTypes)].map(type => {
          if (type === 'ONSHORE') return 'ON-SHORE';
          if (type === 'OFFSHORE') return 'OFF-SHORE';
          return type;
        });
      }

      // Reportee Types (array) - Transform ONSHORE/OFFSHORE to ON-SHORE/OFF-SHORE for API
      if (advancedFilters.reporteeTypes && advancedFilters.reporteeTypes.length > 0) {
        searchParams.reporteeTypes = [...new Set(advancedFilters.reporteeTypes)].map(type => {
          if (type === 'ONSHORE') return 'ON-SHORE';
          if (type === 'OFFSHORE') return 'OFF-SHORE';
          return type;
        });
      }

      // Reconciliation Statuses (array)
      if (advancedFilters.reconciliationStatuses && advancedFilters.reconciliationStatuses.length > 0) {
        searchParams.reconciliationStatuses = [...new Set(advancedFilters.reconciliationStatuses)];
      }

      // DRL number (supports wildcards)
      // If user enters text without *, it's exact match
      // If user enters text with *, it's regex/wildcard match
      if (advancedFilters.drlNumber && advancedFilters.drlNumber.trim() !== '') {
        searchParams.drlNumber = advancedFilters.drlNumber.trim().toUpperCase();
      } else if (searchTerm && searchTerm.trim() !== '') {
        // Use searchTerm as drlNumber if no explicit drlNumber filter
        searchParams.drlNumber = searchTerm.trim().toUpperCase();
      }

      // Clarification type (supports wildcards)
      // If user enters text without *, it's exact match
      // If user enters text with *, it's regex/wildcard match
      if (advancedFilters.clarificationType && advancedFilters.clarificationType.trim() !== '') {
        searchParams.clarificationType = advancedFilters.clarificationType.trim();
      }

      // Unknown filter
      if (advancedFilters.unknown !== undefined && advancedFilters.unknown !== null) {
        searchParams.unknown = advancedFilters.unknown;
      }

      // Date filters - use drlReceivedDateFrom/To, resolvedDateFrom/To, openedDateFrom/To
      // Check if user has set any date filters
      const hasDrlReceivedDateFilter = advancedFilters.drlReceivedDateFrom || advancedFilters.drlReceivedDateTo;
      const hasResolvedDateFilter = advancedFilters.resolvedDateFrom || advancedFilters.resolvedDateTo;
      const hasOpenedDateFilter = advancedFilters.openedDateFrom || advancedFilters.openedDateTo;
      
      // Check if any advanced filters are active
      const isAdvancedFiltersActive = (
        (advancedFilters.statuses && advancedFilters.statuses.length > 0) ||
        (advancedFilters.practiceCodes && advancedFilters.practiceCodes.length > 0) ||
        (advancedFilters.locationCodes && advancedFilters.locationCodes.length > 0) ||
        (advancedFilters.assigneeIds && advancedFilters.assigneeIds.length > 0) ||
        (advancedFilters.excludeAssigneeIds && advancedFilters.excludeAssigneeIds.length > 0) ||
        (advancedFilters.reporterIds && advancedFilters.reporterIds.length > 0) ||
        (advancedFilters.excludeReporterIds && advancedFilters.excludeReporterIds.length > 0) ||
        (advancedFilters.reporteeIds && advancedFilters.reporteeIds.length > 0) ||
        (advancedFilters.excludeReporteeIds && advancedFilters.excludeReporteeIds.length > 0) ||
        (advancedFilters.assigneeTypes && advancedFilters.assigneeTypes.length > 0) ||
        (advancedFilters.reporteeTypes && advancedFilters.reporteeTypes.length > 0) ||
        (advancedFilters.reconciliationStatuses && advancedFilters.reconciliationStatuses.length > 0) ||
        (advancedFilters.drlNumber && advancedFilters.drlNumber.trim() !== '') ||
        (advancedFilters.clarificationType && advancedFilters.clarificationType.trim() !== '') ||
        (advancedFilters.unknown !== undefined && advancedFilters.unknown !== null) ||
        hasDrlReceivedDateFilter ||
        hasResolvedDateFilter ||
        hasOpenedDateFilter
      );
      
      // Get month range for default drlReceivedDate (only if no advanced filters are active)
      const monthRange = getMonthRange(selectedMonth, selectedYear);
      
      // DRL Received Date filters - only add if user explicitly set them
      if (hasDrlReceivedDateFilter) {
        // User explicitly set DRL Received Date filters
        if (advancedFilters.drlReceivedDateFrom && advancedFilters.drlReceivedDateFrom.trim() !== '') {
          searchParams.drlReceivedDateFrom = advancedFilters.drlReceivedDateFrom;
        }
        if (advancedFilters.drlReceivedDateTo && advancedFilters.drlReceivedDateTo.trim() !== '') {
          searchParams.drlReceivedDateTo = advancedFilters.drlReceivedDateTo;
        }
      } else if (!isAdvancedFiltersActive) {
        // Only use default month range if NO advanced filters are active
        searchParams.drlReceivedDateFrom = monthRange.startDate;
        searchParams.drlReceivedDateTo = monthRange.endDate;
      }

      // Resolved Date filters - only add if user explicitly set them
      if (advancedFilters.resolvedDateFrom && advancedFilters.resolvedDateFrom.trim() !== '') {
        searchParams.resolvedDateFrom = advancedFilters.resolvedDateFrom;
      }
      if (advancedFilters.resolvedDateTo && advancedFilters.resolvedDateTo.trim() !== '') {
        searchParams.resolvedDateTo = advancedFilters.resolvedDateTo;
      }

      // Opened Date filters - only add if user explicitly set them
      if (hasOpenedDateFilter) {
        if (advancedFilters.openedDateFrom) {
          searchParams.openedDateFrom = advancedFilters.openedDateFrom;
        }
        if (advancedFilters.openedDateTo) {
          searchParams.openedDateTo = advancedFilters.openedDateTo;
        }
      }

      // Legacy date filters mapping - map to drlReceivedDate instead of openedDate
      if (advancedFilters.startDate && !searchParams.drlReceivedDateFrom) {
        searchParams.drlReceivedDateFrom = advancedFilters.startDate;
      }
      if (advancedFilters.endDate && !searchParams.drlReceivedDateTo) {
        searchParams.drlReceivedDateTo = advancedFilters.endDate;
      }

      // Use DRL clarifications dashboard search endpoint
      const response = await api.searchDrlClarificationsDashboard(searchParams);
      
      let clarificationsList = response.items || [];
      const totalPages = response.totalPages || 0;
      const totalElements = response.totalElements || 0;
      
      // Client-side sorting
      if (sortField && clarificationsList.length > 0) {
        clarificationsList = [...clarificationsList].sort((a, b) => {
          let aVal = a[sortField];
          let bVal = b[sortField];
          
          if (sortField === 'openedAt' || sortField === 'resolvedAt' || sortField === 'drlReceivedDate' || sortField === 'createdAt' || sortField === 'updatedAt') {
            aVal = aVal ? new Date(aVal) : new Date(0);
            bVal = bVal ? new Date(bVal) : new Date(0);
          } else {
            aVal = aVal || '';
            bVal = bVal || '';
          }
          
          if (sortDirection === 'asc') {
            return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
          } else {
            return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
          }
        });
      }
      
      // Store all fetched clarifications
      setAllClarifications(clarificationsList);
      
      setTotalElements(totalElements);
      setTotalPages(totalPages);
      setHasMore(page < totalPages - 1);
      setCurrentPage(page);
      setPageInputValue((page + 1).toString());
      
    } catch (err) {
      console.error('Error fetching DRL clarifications:', err);
      const errorMessage = err?.message || err?.data?.message || 'Failed to load DRL clarifications. Please try again.';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [selectedStatus, searchTerm, advancedFilters, sortField, sortDirection, selectedMonth, selectedYear, showError]);

  // Track previous filter values to detect changes
  const prevFiltersRef = useRef({
    selectedStatus,
    advancedFilters: JSON.stringify(advancedFilters),
    selectedMonth,
    selectedYear
  });

  // Fetch clarifications when page or filters change (excluding searchTerm - handled client-side)
  // Reset to page 0 when filters change, but not when only page changes
  useEffect(() => {
    const filtersChanged = 
      prevFiltersRef.current.selectedStatus !== selectedStatus ||
      prevFiltersRef.current.advancedFilters !== JSON.stringify(advancedFilters) ||
      prevFiltersRef.current.selectedMonth !== selectedMonth ||
      prevFiltersRef.current.selectedYear !== selectedYear;

    if (filtersChanged) {
      // Reset to page 0 when filters change
      if (currentPage !== 0) {
        setCurrentPage(0);
        setPageInputValue('1');
        // Update ref for next comparison
        prevFiltersRef.current = {
          selectedStatus,
          advancedFilters: JSON.stringify(advancedFilters),
          selectedMonth,
          selectedYear
        };
        return; // Don't fetch yet, let the page reset trigger the fetch
      }
    }

    // Update ref for next comparison
    prevFiltersRef.current = {
      selectedStatus,
      advancedFilters: JSON.stringify(advancedFilters),
      selectedMonth,
      selectedYear
    };

    // Fetch data
    fetchClarifications(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedStatus, advancedFilters, sortField, sortDirection, selectedMonth, selectedYear]);

  const handlePreviousPage = () => {
    if (currentPage > 0 && !loading) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (hasMore && !loading) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePageInputChange = (e) => {
    const value = e.target.value;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      setPageInputValue(value);
    }
  };

  const handlePageInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleGoToPage();
    }
  };

  const handleGoToPage = () => {
    if (pageInputValue === '' || loading) return;
    
    const pageNum = parseInt(pageInputValue, 10);
    const maxPage = totalPages > 0 ? totalPages - 1 : 0;
    
    // Validate page number (convert to 0-based index)
    if (pageNum >= 1 && pageNum <= maxPage + 1) {
      setCurrentPage(pageNum - 1);
    } else {
      // Reset to current page if invalid
      setPageInputValue((currentPage + 1).toString());
    }
  };

  // Update page input when currentPage changes externally
  useEffect(() => {
    setPageInputValue((currentPage + 1).toString());
  }, [currentPage]);

  // Calculate pagination display values
  const pageSize = 50;
  // If search is active, show filtered results count, otherwise show total from API
  const displayTotal = searchTerm ? clarifications.length : totalElements;
  const startIndex = searchTerm ? 1 : (currentPage * pageSize + 1);
  const endIndex = searchTerm ? clarifications.length : Math.min((currentPage + 1) * pageSize, totalElements);

  // Client-side filtering for search term
  useEffect(() => {
    if (!searchTerm) {
      setClarifications(allClarifications);
      return;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    const filtered = allClarifications.filter(clarification => {
      // Search by DRL number
      if (clarification.drlNumber && clarification.drlNumber.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search by clarification type
      if (clarification.clarificationType && clarification.clarificationType.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search by details
      if (clarification.details && clarification.details.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      return false;
    });

    setClarifications(filtered);
  }, [searchTerm, allClarifications]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleApplyAdvancedFilters = (filters) => {
    setAdvancedFilters(filters);
  };

  const handleResetAdvancedFilters = () => {
    setAdvancedFilters({
      statuses: [],
      practiceCodes: [],
      locationCodes: [],
      assigneeIds: [],
      excludeAssigneeIds: [],
      reporterIds: [],
      excludeReporterIds: [],
      reporteeIds: [],
      excludeReporteeIds: [],
      assigneeTypes: [],
      reporteeTypes: [],
      reconciliationStatuses: [],
      drlNumber: '',
      clarificationType: '',
      unknown: undefined,
      drlReceivedDateFrom: '',
      drlReceivedDateTo: '',
      resolvedDateFrom: '',
      resolvedDateTo: '',
      openedDateFrom: '',
      openedDateTo: '',
      startDate: '',
      endDate: '',
      month: null,
      year: null
    });
  };

  const handleClearAllFilters = () => {
    // Reset advanced filters
    handleResetAdvancedFilters();
    // Reset basic filters
    setSelectedStatus('');
    setSearchTerm('');
    // Close advanced filter drawer
    setShowAdvancedFilters(false);
    // Clear from storage
    sessionStorage.removeItem(STORAGE_KEY);
  };

  // Check if any advanced filter is active
  const hasAdvancedFilters = () => {
    return (
      (advancedFilters.statuses && advancedFilters.statuses.length > 0) ||
      (advancedFilters.practiceCodes && advancedFilters.practiceCodes.length > 0) ||
      (advancedFilters.locationCodes && advancedFilters.locationCodes.length > 0) ||
      (advancedFilters.assigneeIds && advancedFilters.assigneeIds.length > 0) ||
      (advancedFilters.excludeAssigneeIds && advancedFilters.excludeAssigneeIds.length > 0) ||
      (advancedFilters.reporterIds && advancedFilters.reporterIds.length > 0) ||
      (advancedFilters.excludeReporterIds && advancedFilters.excludeReporterIds.length > 0) ||
      (advancedFilters.reporteeIds && advancedFilters.reporteeIds.length > 0) ||
      (advancedFilters.excludeReporteeIds && advancedFilters.excludeReporteeIds.length > 0) ||
      (advancedFilters.assigneeTypes && advancedFilters.assigneeTypes.length > 0) ||
      (advancedFilters.reporteeTypes && advancedFilters.reporteeTypes.length > 0) ||
      (advancedFilters.reconciliationStatuses && advancedFilters.reconciliationStatuses.length > 0) ||
      (advancedFilters.drlNumber && advancedFilters.drlNumber.trim() !== '') ||
      (advancedFilters.clarificationType && advancedFilters.clarificationType.trim() !== '') ||
      (advancedFilters.unknown !== undefined && advancedFilters.unknown !== null) ||
      (advancedFilters.drlReceivedDateFrom && advancedFilters.drlReceivedDateFrom.trim() !== '') ||
      (advancedFilters.drlReceivedDateTo && advancedFilters.drlReceivedDateTo.trim() !== '') ||
      (advancedFilters.resolvedDateFrom && advancedFilters.resolvedDateFrom.trim() !== '') ||
      (advancedFilters.resolvedDateTo && advancedFilters.resolvedDateTo.trim() !== '') ||
      (advancedFilters.openedDateFrom && advancedFilters.openedDateFrom.trim() !== '') ||
      (advancedFilters.openedDateTo && advancedFilters.openedDateTo.trim() !== '') ||
      (advancedFilters.startDate && advancedFilters.startDate.trim() !== '') ||
      (advancedFilters.endDate && advancedFilters.endDate.trim() !== '') ||
      advancedFilters.month ||
      advancedFilters.year
    );
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'RESOLVED': 'status-resolved',
      'OPEN': 'status-open'
    };
    return statusMap[status] || 'status-open';
  };

  const formatStatus = (status) => {
    const statusMap = {
      'OPEN': 'Open',
      'RESOLVED': 'Resolved'
    };
    return statusMap[status] || status || 'Open';
  };

  const getReconciliationStatusClass = (reconciliationStatus) => {
    const statusMap = {
      'RECONCILED': 'status-complete',
      'OVER_RECONCILED': 'status-over-posted',
      'UNRECONCILED': 'status-pending'
    };
    return statusMap[reconciliationStatus] || 'status-pending';
  };

  const formatReconciliationStatus = (reconciliationStatus) => {
    const statusMap = {
      'RECONCILED': 'Reconciled',
      'OVER_RECONCILED': 'Excess Reconciled',
      'UNRECONCILED': 'Pending Reconciliation'
    };
    return statusMap[reconciliationStatus] || reconciliationStatus || 'N/A';
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // getUserName is now provided by useUsers hook from context

  const getDateRangeDisplay = () => {
    // If both start and end dates are selected, show the date range
    if (advancedFilters.startDate && advancedFilters.endDate) {
      return formatDateRange(advancedFilters.startDate, advancedFilters.endDate);
    }
    // If advanced filters are active (but no date range), show custom range
    if (hasAdvancedFilters()) {
      return 'Custom Range';
    }
    // Show month/year format instead of date range
    return formatMonthYear(selectedMonth, selectedYear);
  };

  const SortArrow = ({ field }) => {
    if (sortField !== field) return null;
    return (
      <span className="sort-arrow">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  return (
    <div className="clarifications-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1 className="page-title">DRL Clarifications</h1>
          <p className="page-subtitle">Manage and track DRL clarifications</p>
        </div>
        <div className="header-actions">
          {!hasAdvancedFilters() && (
            <div className="date-range-container">
              <button 
                className="date-range-btn"
                onClick={() => setShowMonthPicker(!showMonthPicker)}
              >
                {getDateRangeDisplay()}
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{ marginLeft: '8px' }}>
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {showMonthPicker && (
                <MonthYearPicker
                  selectedMonth={selectedMonth}
                  selectedYear={selectedYear}
                  onSelect={handleMonthYearSelect}
                  onClose={() => setShowMonthPicker(false)}
                />
              )}
            </div>
          )}
          {hasAdvancedFilters() && (
            <div className="date-range-container">
              <div className="date-range-display">
                {getDateRangeDisplay()}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="filters-section">
        <div className="search-bar">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="2"/>
            <path d="M15 15L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            placeholder="Search by DRL number (e.g., DRL* or DRL), type, details..."
            value={searchTerm}
            onChange={(e) => {
              const filteredValue = filterEmojis(e.target.value);
              setSearchTerm(filteredValue);
            }}
          />
        </div>
        <div className="filter-dropdowns">
          <SearchableDropdown
            options={[
              { value: '', label: 'All Statuses' },
              { value: 'OPEN', label: 'Open' },
              { value: 'RESOLVED', label: 'Resolved' }
            ]}
            value={selectedStatus}
            onChange={(value) => setSelectedStatus(value)}
            placeholder="All Statuses"
            maxVisibleItems={5}
          />
          <button 
            className={`btn-advanced-filter ${hasAdvancedFilters() ? 'active' : ''}`}
            onClick={() => setShowAdvancedFilters(true)}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M3 5H17M7 10H17M11 15H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Advanced Filters
          </button>
        </div>
      </div>

      {/* Active Filters Indicator */}
      {(hasAdvancedFilters() || selectedStatus || searchTerm) && (
        <div className="active-filters-section">
          <div className="active-filters-label">Active Filters:</div>
          <div className="active-filters-chips">
            {searchTerm && (
              <div className="filter-chip">
                <span>Search: {searchTerm}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => setSearchTerm('')}
                  title="Remove search filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {selectedStatus && (
              <div className="filter-chip">
                <span>Status: {[
                  { value: 'OPEN', label: 'Open' },
                  { value: 'RESOLVED', label: 'Resolved' }
                ].find(s => s.value === selectedStatus)?.label || selectedStatus}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => setSelectedStatus('')}
                  title="Remove status filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {/* Multiple Status Filters */}
            {advancedFilters.statuses && advancedFilters.statuses.length > 0 && advancedFilters.statuses.map((status) => {
              const statusLabels = {
                'OPEN': 'Open',
                'RESOLVED': 'Resolved'
              };
              return (
                <div key={status} className="filter-chip">
                  <span>Status: {statusLabels[status] || status}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      setAdvancedFilters(prev => ({
                        ...prev,
                        statuses: prev.statuses.filter(s => s !== status)
                      }));
                    }}
                    title="Remove status filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              );
            })}
            {/* Multiple Practice Filters */}
            {advancedFilters.practiceCodes && advancedFilters.practiceCodes.length > 0 && advancedFilters.practiceCodes.map((practiceCode) => (
              <div key={practiceCode} className="filter-chip">
                <span>Practice: {practices.find(p => p.code === practiceCode)?.name || practiceCode}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      practiceCodes: prev.practiceCodes.filter(p => p !== practiceCode),
                      locationCodes: prev.locationCodes.filter(loc => {
                        // Remove locations that belong to this practice
                        const practice = practices.find(p => p.code === practiceCode);
                        if (practice && practice.practiceId) {
                          // This would need location data to check, for now just remove all
                          return false;
                        }
                        return true;
                      })
                    }));
                  }}
                  title="Remove practice filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {/* Multiple Location Filters */}
            {advancedFilters.locationCodes && advancedFilters.locationCodes.length > 0 && advancedFilters.locationCodes.map((locationCode) => (
              <div key={locationCode} className="filter-chip">
                <span>Location: {locationCode}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      locationCodes: prev.locationCodes.filter(l => l !== locationCode)
                    }));
                  }}
                  title="Remove location filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {/* Multiple Assignee Filters */}
            {advancedFilters.assigneeIds && advancedFilters.assigneeIds.length > 0 && advancedFilters.assigneeIds.map((assigneeId) => (
              <div key={assigneeId} className="filter-chip">
                <span>Assignee: {getUserName(assigneeId)}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      assigneeIds: prev.assigneeIds.filter(id => id !== assigneeId)
                    }));
                  }}
                  title="Remove assignee filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {advancedFilters.excludeAssigneeIds && advancedFilters.excludeAssigneeIds.length > 0 && advancedFilters.excludeAssigneeIds.map((assigneeId) => (
              <div key={`exclude-assignee-${assigneeId}`} className="filter-chip filter-chip--exclude">
                <span>Assignee: Not {getUserName(assigneeId)}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      excludeAssigneeIds: prev.excludeAssigneeIds.filter(id => id !== assigneeId)
                    }));
                  }}
                  title="Remove excluded assignee filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {/* Multiple Reporter Filters */}
            {advancedFilters.reporterIds && advancedFilters.reporterIds.length > 0 && advancedFilters.reporterIds.map((reporterId) => (
              <div key={reporterId} className="filter-chip">
                <span>Reporter: {getUserName(reporterId)}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      reporterIds: prev.reporterIds.filter(id => id !== reporterId)
                    }));
                  }}
                  title="Remove reporter filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {advancedFilters.excludeReporterIds && advancedFilters.excludeReporterIds.length > 0 && advancedFilters.excludeReporterIds.map((reporterId) => (
              <div key={`exclude-reporter-${reporterId}`} className="filter-chip filter-chip--exclude">
                <span>Reporter: Not {getUserName(reporterId)}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      excludeReporterIds: prev.excludeReporterIds.filter(id => id !== reporterId)
                    }));
                  }}
                  title="Remove excluded reporter filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {/* Multiple Reportee Filters (for DIT/DRL clarifications) */}
            {advancedFilters.reporteeIds && advancedFilters.reporteeIds.length > 0 && advancedFilters.reporteeIds.map((reporteeId) => (
              <div key={reporteeId} className="filter-chip">
                <span>Reporter: {getUserName(reporteeId)}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      reporteeIds: prev.reporteeIds.filter(id => id !== reporteeId)
                    }));
                  }}
                  title="Remove reporter filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {advancedFilters.excludeReporteeIds && advancedFilters.excludeReporteeIds.length > 0 && advancedFilters.excludeReporteeIds.map((reporteeId) => (
              <div key={`exclude-reportee-${reporteeId}`} className="filter-chip filter-chip--exclude">
                <span>Reporter: Not {getUserName(reporteeId)}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      excludeReporteeIds: prev.excludeReporteeIds.filter(id => id !== reporteeId)
                    }));
                  }}
                  title="Remove excluded reporter filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {/* Multiple Assignee Type Filters */}
            {advancedFilters.assigneeTypes && advancedFilters.assigneeTypes.length > 0 && advancedFilters.assigneeTypes.map((assigneeType) => {
              const assigneeTypeLabels = {
                'ONSHORE': 'Onshore',
                'OFFSHORE': 'Offshore'
              };
              return (
                <div key={assigneeType} className="filter-chip">
                  <span>Assignee: {assigneeTypeLabels[assigneeType] || assigneeType}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      setAdvancedFilters(prev => ({
                        ...prev,
                        assigneeTypes: prev.assigneeTypes.filter(at => at !== assigneeType)
                      }));
                    }}
                    title="Remove assignee type filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              );
            })}
            {/* Multiple Reportee Type Filters */}
            {advancedFilters.reporteeTypes && advancedFilters.reporteeTypes.length > 0 && advancedFilters.reporteeTypes.map((reporteeType) => {
              const reporteeTypeLabels = {
                'ONSHORE': 'Onshore',
                'OFFSHORE': 'Offshore'
              };
              return (
                <div key={reporteeType} className="filter-chip">
                  <span>Reporter: {reporteeTypeLabels[reporteeType] || reporteeType}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      setAdvancedFilters(prev => ({
                        ...prev,
                        reporteeTypes: prev.reporteeTypes.filter(rt => rt !== reporteeType)
                      }));
                    }}
                    title="Remove reportee type filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              );
            })}
            {/* Multiple Reconciliation Status Filters */}
            {advancedFilters.reconciliationStatuses && advancedFilters.reconciliationStatuses.length > 0 && advancedFilters.reconciliationStatuses.map((reconciliationStatus) => {
              const reconciliationStatusLabels = {
                'RECONCILED': 'Reconciled',
                'OVER_RECONCILED': 'Excess Reconciled',
                'UNRECONCILED': 'Pending Reconciliation'
              };
              return (
                <div key={reconciliationStatus} className="filter-chip">
                  <span>Reconciliation Status: {reconciliationStatusLabels[reconciliationStatus] || reconciliationStatus}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      setAdvancedFilters(prev => ({
                        ...prev,
                        reconciliationStatuses: prev.reconciliationStatuses.filter(rs => rs !== reconciliationStatus)
                      }));
                    }}
                    title="Remove reconciliation status filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              );
            })}
            {advancedFilters.drlNumber && (
              <div className="filter-chip">
                <span>DRL Number: {advancedFilters.drlNumber}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({ ...prev, drlNumber: '' }));
                  }}
                  title="Remove DRL number filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {advancedFilters.clarificationType && (
              <div className="filter-chip">
                <span>Clarification Type: {advancedFilters.clarificationType}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({ ...prev, clarificationType: '' }));
                  }}
                  title="Remove clarification type filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {advancedFilters.unknown !== undefined && advancedFilters.unknown !== null && (
              <div className="filter-chip">
                <span>Unknown: {advancedFilters.unknown === true ? 'True' : 'False'}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({ ...prev, unknown: undefined }));
                  }}
                  title="Remove unknown filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {(advancedFilters.drlReceivedDateFrom || advancedFilters.drlReceivedDateTo) && (
              <div className="filter-chip">
                <span>DRL Received Date: {formatDateRange(
                  advancedFilters.drlReceivedDateFrom || '',
                  advancedFilters.drlReceivedDateTo || ''
                )}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      drlReceivedDateFrom: '',
                      drlReceivedDateTo: ''
                    }));
                  }}
                  title="Remove DRL received date range filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {(advancedFilters.resolvedDateFrom || advancedFilters.resolvedDateTo) && (
              <div className="filter-chip">
                <span>Resolved Date: {formatDateRange(
                  advancedFilters.resolvedDateFrom || '',
                  advancedFilters.resolvedDateTo || ''
                )}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      resolvedDateFrom: '',
                      resolvedDateTo: ''
                    }));
                  }}
                  title="Remove resolved date range filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {(advancedFilters.openedDateFrom || advancedFilters.openedDateTo || advancedFilters.startDate || advancedFilters.endDate) && (
              <div className="filter-chip">
                <span>Opened Date: {formatDateRange(
                  advancedFilters.openedDateFrom || advancedFilters.startDate || '',
                  advancedFilters.openedDateTo || advancedFilters.endDate || ''
                )}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      openedDateFrom: '',
                      openedDateTo: '',
                      startDate: '',
                      endDate: ''
                    }));
                  }}
                  title="Remove date range filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {(advancedFilters.month || advancedFilters.year) && (
              <div className="filter-chip">
                <span>Period: {formatMonthYear(advancedFilters.month || selectedMonth, advancedFilters.year || selectedYear)}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({ ...prev, month: null, year: null }));
                  }}
                  title="Remove period filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            <button
              className="clear-all-filters-btn"
              onClick={handleClearAllFilters}
              title="Clear all filters"
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="table-section">
        <div className="table-header">
          <div className="table-header-left">
            {totalElements > 0 && (
              <div className="pagination-info">
                <button
                  className="pagination-arrow"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 0 || loading}
                  title="Previous page"
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                    <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <span className="pagination-text">
                  {startIndex} - {endIndex}, of {displayTotal}
                </span>
                {totalPages > 1 && (
                  <div className="pagination-page-input">
                    <span style={{ fontSize: '12px', color: '#6b7280', marginRight: '4px' }}>Page:</span>
                    <input
                      type="text"
                      value={pageInputValue}
                      onChange={handlePageInputChange}
                      onKeyPress={handlePageInputKeyPress}
                      onBlur={handleGoToPage}
                      style={{
                        width: '45px',
                        padding: '2px 4px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        fontSize: '12px',
                        textAlign: 'center',
                        height: '24px'
                      }}
                    />
                    <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }}>
                      of {totalPages}
                    </span>
                  </div>
                )}
                <button
                  className="pagination-arrow"
                  onClick={handleNextPage}
                  disabled={!hasMore || loading}
                  title="Next page"
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                    <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
          <div className="table-actions">
            <Tooltip text="Reset Column widths" position="top">
              <button
                type="button"
                className="icon-btn"
                onClick={resetColumnWidths}
                aria-label="Reset Column widths"
              >
                <RotateCcw size={18} strokeWidth={2} />
              </button>
            </Tooltip>
            <button 
              ref={columnSelectorTriggerRef}
              className="icon-btn"
              onClick={() => setShowColumnSelector(!showColumnSelector)}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3 5H17M3 10H17M3 15H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="table-wrapper">
          <table className="clarifications-table clarifications-table--col-resize">
            <Colgroup />
            <thead>
              <tr>
                {visibleColumns.map(colKey => {
                  const column = allColumns.find(col => col.key === colKey);
                  if (!column) return null;

                  switch (colKey) {
                    case 'drlNumber':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('drlNumber')}
                        >
                          DRL Number <SortArrow field="drlNumber" />
                        </ResizableTh>
                      );
                    case 'clarificationType':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('clarificationType')}
                        >
                          Type <SortArrow field="clarificationType" />
                        </ResizableTh>
                      );
                    case 'drlReceivedDate':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('drlReceivedDate')}
                        >
                          DRL Received Date <SortArrow field="drlReceivedDate" />
                        </ResizableTh>
                      );
                    case 'amount':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('amount')}
                        >
                          Amount <SortArrow field="amount" />
                        </ResizableTh>
                      );
                    case 'details':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Details
                        </ResizableTh>
                      );
                    case 'assigneeType':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('assignee')}
                        >
                          Assignee <SortArrow field="assignee" />
                        </ResizableTh>
                      );
                    case 'assignee':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Assignee Name
                        </ResizableTh>
                      );
                    case 'reporteeType':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('reportee')}
                        >
                          Reporter <SortArrow field="reportee" />
                        </ResizableTh>
                      );
                    case 'reporter':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Reporter Name
                        </ResizableTh>
                      );
                    case 'status':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('status')}
                        >
                          Status <SortArrow field="status" />
                        </ResizableTh>
                      );
                    case 'reconciliationStatus':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('reconciliationStatus')}
                        >
                          Reconciliation Status <SortArrow field="reconciliationStatus" />
                        </ResizableTh>
                      );
                    case 'openedAt':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('openedAt')}
                        >
                          Opened Date <SortArrow field="openedAt" />
                        </ResizableTh>
                      );
                    case 'resolvedAt':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('resolvedAt')}
                        >
                          Resolved Date <SortArrow field="resolvedAt" />
                        </ResizableTh>
                      );
                    case 'createdAt':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('createdAt')}
                        >
                          Created At <SortArrow field="createdAt" />
                        </ResizableTh>
                      );
                    case 'updatedAt':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('updatedAt')}
                        >
                          Updated At <SortArrow field="updatedAt" />
                        </ResizableTh>
                      );
                    default:
                      return null;
                  }
                })}
              </tr>
            </thead>
            <tbody>
              {loading && clarifications.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length} style={{ textAlign: 'center', padding: '40px' }}>
                    Loading clarifications...
                  </td>
                </tr>
              ) : clarifications.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length} style={{ textAlign: 'center', padding: '40px' }}>
                    No clarifications found
                  </td>
                </tr>
              ) : (
                clarifications.map((clarification) => (
                  <tr
                    key={clarification.clarificationId}
                    className="clarifications-table-row--clickable"
                    onClick={() => openDrlClarificationDetail(clarification)}
                  >
                    {visibleColumns.map(colKey => {
                      switch (colKey) {
                        case 'drlNumber':
                          return (
                            <td key={colKey} className="clarification-id-table-cell">
                              {clarification.drlNumber ? (
                                <div className="clarification-id-cell-inner">
                                  <button
                                    type="button"
                                    className="clarifications-id-copy-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCopyDrlNumber(clarification.drlNumber);
                                    }}
                                    title="Copy DRL number"
                                    aria-label="Copy DRL number"
                                  >
                                    <Copy size={16} aria-hidden />
                                  </button>
                                  <div style={{ minWidth: 0, flex: 1 }}>
                                    <CellEllipsis text={clarification.drlNumber} />
                                  </div>
                                </div>
                              ) : (
                                <CellEllipsis text="N/A" />
                              )}
                            </td>
                          );
                        case 'clarificationType':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={clarification.clarificationType || 'N/A'} />
                            </td>
                          );
                        case 'drlReceivedDate':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={clarification.drlReceivedDate ? formatDateUS(clarification.drlReceivedDate) : 'N/A'} />
                            </td>
                          );
                        case 'amount':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={formatCurrency(clarification.amount)} />
                            </td>
                          );
                        case 'details':
                          return (
                            <td key={colKey} className="details-cell">
                              <CellEllipsis text={clarification.details || ''} />
                            </td>
                          );
                        case 'assignee':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={getUserName(clarification.assigneeId)} />
                            </td>
                          );
                        case 'assigneeType':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={clarification.assignee || 'N/A'} />
                            </td>
                          );
                        case 'reporter':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={getUserName(clarification.reporterId)} />
                            </td>
                          );
                        case 'reporteeType':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={clarification.reportee || 'N/A'} />
                            </td>
                          );
                        case 'status':
                          return (
                            <td key={colKey}>
                              <span className={`status-badge ${getStatusClass(clarification.status)}`}>
                                <CellEllipsis text={formatStatus(clarification.status)} />
                              </span>
                            </td>
                          );
                        case 'reconciliationStatus':
                          return (
                            <td key={colKey}>
                              <span className={`status-badge ${getReconciliationStatusClass(clarification.reconciliationStatus)}`}>
                                <CellEllipsis text={formatReconciliationStatus(clarification.reconciliationStatus)} />
                              </span>
                            </td>
                          );
                        case 'openedAt':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={clarification.openedAt ? formatDateTime(clarification.openedAt) : 'N/A'} />
                            </td>
                          );
                        case 'resolvedAt':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={clarification.resolvedAt ? formatDateTime(clarification.resolvedAt) : 'N/A'} />
                            </td>
                          );
                        case 'createdAt':
                          return (
                            <td key={colKey} className="td-timestamp-cell">
                              <UserTimestamp
                                userId={clarification.createdBy}
                                dateTime={clarification.createdAt}
                                action="created"
                                getUserName={getUserName}
                                getUserById={getUserById}
                              />
                            </td>
                          );
                        case 'updatedAt':
                          return (
                            <td key={colKey} className="td-timestamp-cell">
                              <UserTimestamp
                                userId={clarification.updatedBy}
                                dateTime={clarification.updatedAt}
                                action="updated"
                                getUserName={getUserName}
                                getUserById={getUserById}
                              />
                            </td>
                          );
                        default:
                          return null;
                      }
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

      </div>

      <AdvancedFilterDrawer
        isOpen={showAdvancedFilters}
        onClose={() => setShowAdvancedFilters(false)}
        filters={advancedFilters}
        onApplyFilters={handleApplyAdvancedFilters}
        onResetFilters={handleResetAdvancedFilters}
        isClarifications={true}
        isDitDrl={true}
      />

      {showColumnSelector && (
        <ColumnSelector
          availableColumns={allColumns}
          visibleColumns={visibleColumns}
          defaultColumns={defaultColumns}
          onChange={(cols) => {
            setVisibleColumns(cols);
            persistVisibleColumns(DRL_CLARIFICATIONS_COLUMN_PREFS_KEY, cols);
          }}
          onClose={() => setShowColumnSelector(false)}
          triggerRef={columnSelectorTriggerRef}
        />
      )}
    </div>
  );
};

export default DrlClarifications;
